import { useState, useEffect, createContext, useContext } from "react";
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { Toaster, toast } from "sonner";
import LoginPage from "./pages/LoginPage";
import AdminDashboard from "./pages/AdminDashboard";
import EmployeeDashboard from "./pages/EmployeeDashboard";
import TstDashboard from "./pages/TstDashboard";
import CobrancaDashboard from "./pages/CobrancaDashboard";
import PayslipViewer from "./pages/PayslipViewer";
import PublicCertificateValidation from "./pages/PublicCertificateValidation";
import PublicListValidation from "./pages/PublicListValidation";
import PublicVacationValidation from "./pages/PublicVacationValidation";
import PublicAdmissionKitValidation from "./pages/PublicAdmissionKitValidation";
import PublicSupervisorSignature from "./pages/PublicSupervisorSignature";
import PublicForm from "./pages/PublicForm";
import PrivacyPolicy from "./pages/PrivacyPolicy";
import "@/App.css";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

// Auth Context
const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};

const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Check for stored auth on mount
    try {
      const storedToken = localStorage.getItem("token");
      const storedUser = localStorage.getItem("user");
      
      if (storedToken && storedUser) {
        const parsedUser = JSON.parse(storedUser);
        setToken(storedToken);
        setUser(parsedUser);
      }
    } catch (error) {
      // Clear invalid data
      localStorage.removeItem("token");
      localStorage.removeItem("user");
    }
    setLoading(false);
  }, []);

  // Global axios 401 interceptor — when the JWT expires, ANY request
  // that hits the backend returns 401 with detail="Token expirado".
  // Without an interceptor, each component shows its own toast (toast
  // spam) and the UI freezes in a half-loaded state. Centralizing here:
  //   1. Dedupes the "session expired" toast (only one is shown)
  //   2. Clears local auth state immediately
  //   3. Redirects to / so the user re-authenticates
  // Public endpoints (login, public PDF preview, etc.) are ignored.
  useEffect(() => {
    const interceptor = axios.interceptors.response.use(
      (resp) => resp,
      async (error) => {
        const status = error?.response?.status;
        let detail = error?.response?.data?.detail || "";
        // Downloads use responseType:"blob" — the 401 body comes as a
        // Blob, so parse it to detect "Token expirado".
        if (!detail && status === 401 && error?.response?.data instanceof Blob) {
          try {
            detail = JSON.parse(await error.response.data.text()).detail || "";
          } catch (_) {
            /* ignore */
          }
        }
        const url = error?.config?.url || "";
        const isAuthError =
          status === 401 &&
          (detail.toLowerCase().includes("token") ||
            detail.toLowerCase().includes("expirado") ||
            detail.toLowerCase().includes("autenticação"));
        const isPublic =
          url.includes("/auth/") ||
          url.includes("/login") ||
          url.includes("/public/");
        if (isAuthError && !isPublic && localStorage.getItem("token")) {
          if (!window.__avi_session_expired_toast_shown) {
            window.__avi_session_expired_toast_shown = true;
            toast.error("Sua sessão expirou. Faça login novamente.", {
              id: "session-expired",
              duration: 4000,
            });
            // Reset the flag a few seconds later so future expirations
            // (e.g. after re-login) can still notify.
            setTimeout(() => {
              window.__avi_session_expired_toast_shown = false;
            }, 6000);
          }
          localStorage.removeItem("token");
          localStorage.removeItem("user");
          setUser(null);
          setToken(null);
          if (window.location.pathname !== "/") {
            // Slight delay so the toast is visible before redirect.
            setTimeout(() => {
              window.location.replace("/");
            }, 300);
          }
        }
        return Promise.reject(error);
      },
    );
    return () => axios.interceptors.response.eject(interceptor);
  }, []);

  const login = (userData, authToken) => {
    setUser(userData);
    setToken(authToken);
    localStorage.setItem("user", JSON.stringify(userData));
    localStorage.setItem("token", authToken);
  };

  const updateUser = (updates) => {
    setUser((prev) => {
      const newUser = { ...(prev || {}), ...updates };
      localStorage.setItem("user", JSON.stringify(newUser));
      return newUser;
    });
  };

  const logout = () => {
    setUser(null);
    setToken(null);
    localStorage.removeItem("user");
    localStorage.removeItem("token");
  };

  return (
    <AuthContext.Provider value={{ user, token, login, logout, updateUser, loading }}>
      {children}
    </AuthContext.Provider>
  );
};

// Protected Route Component
const ProtectedRoute = ({ children, allowedTypes }) => {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-900"></div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/" state={{ from: location }} replace />;
  }

  if (allowedTypes && !allowedTypes.includes(user.user_type)) {
    return <Navigate to="/" replace />;
  }

  return children;
};

// Redirect if already logged in
const PublicRoute = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-900"></div>
      </div>
    );
  }

  // If user is logged in, redirect to appropriate dashboard
  if (user) {
    if (user.user_type === "admin") {
      return <Navigate to="/admin" replace />;
    } else if (user.user_type === "tst") {
      return <Navigate to="/tst" replace />;
    } else if (user.user_type === "cobranca") {
      return <Navigate to="/cobranca" replace />;
    } else {
      return <Navigate to="/dashboard" replace />;
    }
  }

  return children;
};

function AppRoutes() {
  return (
    <Routes>
      <Route 
        path="/" 
        element={
          <PublicRoute>
            <LoginPage />
          </PublicRoute>
        } 
      />
      <Route
        path="/admin"
        element={
          <ProtectedRoute allowedTypes={["admin"]}>
            <AdminDashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/tst"
        element={
          <ProtectedRoute allowedTypes={["tst"]}>
            <TstDashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/cobranca"
        element={
          <ProtectedRoute allowedTypes={["cobranca"]}>
            <CobrancaDashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/dashboard"
        element={
          <ProtectedRoute allowedTypes={["employee"]}>
            <EmployeeDashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/payslip/:id"
        element={
          <ProtectedRoute allowedTypes={["employee"]}>
            <PayslipViewer />
          </ProtectedRoute>
        }
      />
      <Route
        path="/verificar/:id"
        element={<PublicCertificateValidation />}
      />
      <Route
        path="/verificar-lista/:id"
        element={<PublicListValidation />}
      />
      <Route
        path="/validar-lista/:id"
        element={<PublicListValidation />}
      />
      <Route
        path="/validar/:signatureId"
        element={<PublicVacationValidation />}
      />
      <Route
        path="/validar"
        element={<PublicVacationValidation />}
      />
      <Route
        path="/validar-kit/:signatureId"
        element={<PublicAdmissionKitValidation />}
      />
      <Route
        path="/validar-kit"
        element={<PublicAdmissionKitValidation />}
      />
      {/* Alias routes — older renderers stamped these specific URLs into
          the QR codes. They all hit the same generic validation page,
          which calls /api/public/validate-kit/{id} (the backend now
          searches across contracts/EPI/badge/VT/vacation/kit_documents). */}
      <Route
        path="/validar-contrato/:signatureId"
        element={<PublicAdmissionKitValidation />}
      />
      <Route
        path="/validar-epi/:signatureId"
        element={<PublicAdmissionKitValidation />}
      />
      <Route
        path="/validar-cracha/:signatureId"
        element={<PublicAdmissionKitValidation />}
      />
      <Route
        path="/validar-vt/:signatureId"
        element={<PublicAdmissionKitValidation />}
      />
      <Route
        path="/validar-rescisao/:signatureId"
        element={<PublicAdmissionKitValidation />}
      />
      <Route
        path="/validar-rescisao"
        element={<PublicAdmissionKitValidation />}
      />
      <Route
        path="/assinar-supervisor/:token"
        element={<PublicSupervisorSignature kind="supervisor" />}
      />
      <Route
        path="/assinar-designado/:token"
        element={<PublicSupervisorSignature kind="designado" />}
      />
      <Route
        path="/privacidade"
        element={<PrivacyPolicy />}
      />
      <Route
        path="/form/:formId"
        element={<PublicForm />}
      />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Toaster 
          position="top-center" 
          richColors 
          closeButton
          toastOptions={{
            style: {
              fontFamily: '"Inter", sans-serif',
            },
            duration: 3000,
          }}
        />
        <AppRoutes />
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
